import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AppProviders, useAuth } from "@/providers/AppProviders";
import { AccountProvider } from "@/providers/AccountProvider";
import { AccountIndexRedirect, AccountLanding, LegacyPathRedirect, ShellOutlet } from "@/components/shell/AccountRouting";
import { ShellSkeleton } from "@/components/shell/AppShell";
import { PageErrorBoundary } from "@/components/shell/PageErrorBoundary";
import { CommandPalette } from "@/components/command-palette/CommandPalette";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import Login from "@/pages/Login";
import { PlatformBrandingSync } from "@/components/brand/PlatformBrandingSync";

// Route-level code splitting: every module is its own chunk, so the initial bundle
// carries only the shell + router.
//
// Routing is ACCOUNT-SCOPED: /accounts/orWQlK0M8BCmMmkqu3cc/contacts.
//
// This file used to say the opposite, and the reason it gave was right at the time: the
// backend resolved the tenant from the bearer token alone, so a segment in the URL would
// have been decorative. That is no longer true -- a person can now hold seats in several
// accounts, and /api/team/me lists them -- so the segment is what decides which one a tab
// is looking at. See providers/AccountProvider.tsx for why the URL rather than state.
//
// The id is the 20-character PUBLIC one. The integer tenant id is the owner's user id and
// is never published; putting it here would say how many accounts exist and what the
// neighbours are numbered.
const ExecutiveOverview = lazy(() => import("@/pages/ExecutiveOverview"));
const Inbox = lazy(() => import("@/pages/Inbox"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Pipelines = lazy(() => import("@/pages/Pipelines"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactDetail = lazy(() => import("@/pages/ContactDetail"));
const AgentStudio = lazy(() => import("@/pages/AgentStudio"));
const AutomationRules = lazy(() => import("@/pages/AutomationRules"));
const Automations = lazy(() => import("@/pages/Automations"));
const FlowCanvas = lazy(() => import("@/pages/FlowCanvas"));
const AgentHistory = lazy(() => import("@/pages/AgentHistory"));
const FormSubmissions = lazy(() => import("@/pages/FormSubmissions"));
const Settings = lazy(() => import("@/pages/Settings"));
const Team = lazy(() => import("@/pages/Team"));
const Developer = lazy(() => import("@/pages/Developer"));
const Invite = lazy(() => import("@/pages/Invite"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
// Social nests under /social/* rather than /v1/account/:acc_id/social/*: the backend
// resolves the tenant from the bearer token and exposes no account-switch endpoint, so
// an :acc_id segment would be decorative -- the same decision taken for every other
// module in this app.
const SocialLayout = lazy(() =>
  import("@/pages/social/SocialLayout").then((m) => ({ default: m.SocialLayout })),
);
const Composer = lazy(() => import("@/pages/social/Composer"));
const Engagement = lazy(() => import("@/pages/social/Engagement"));
const Connections = lazy(() => import("@/pages/Connections"));
const Ads = lazy(() => import("@/pages/Ads"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const Commerce = lazy(() => import("@/pages/Commerce"));
const LeadForms = lazy(() => import("@/pages/LeadForms"));
const Performance = lazy(() => import("@/pages/Performance"));
const CustomFields = lazy(() => import("@/pages/CustomFields"));
const Broadcast = lazy(() => import("@/pages/Broadcast"));
const WhatsAppBillingSettingsPage = lazy(() => import("@/pages/WhatsAppBillingSettingsPage"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminShell = lazy(() => import("@/components/admin/AdminShell").then((m) => ({ default: m.AdminShell })));
const BrandingSettingsPage = lazy(() => import("@/pages/BrandingSettingsPage"));
const PlatformBrandingPage = lazy(() => import("@/pages/admin/PlatformBrandingPage"));
const BillingPage = lazy(() => import("@/pages/admin/BillingPage"));
const AdminTenantAccountPage = lazy(() => import("@/pages/admin/AdminTenantAccountPage"));
const WebviewSessionPage = lazy(() => import("@/pages/WebviewSessionPage"));
const Calls = lazy(() => import("@/pages/Calls"));

// The top-level paths this app answered before accounts existed. Listed rather than
// wildcarded because a bare `*` here would also swallow /accounts/... and loop.
const LEGACY_PREFIXES = [
  "/dashboard", "/inbox", "/opportunities", "/pipeline", "/contacts", "/agent-studio",
  "/social", "/connections", "/ads", "/catalog", "/commerce", "/lead-forms",
  "/performance", "/custom-fields", "/broadcast", "/settings",
  "/calls",
];

function AppRoutes() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { user } = useAuth();
  const { pathname } = useLocation();

  return (
    <>
      {/* The palette navigates between guarded modules, so it only exists once there
          is a session. Mounting it on /login would put a global Cmd+K handler on a
          page whose every destination bounces straight back to /login. */}
      {user != null && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}
      {/* The outer net: pages outside the shells, and a crash in a shell itself. Each shell
          holds its own boundary around its page so a broken page keeps the navigation. */}
      <PageErrorBoundary resetKey={pathname}>
      <Suspense fallback={<ShellSkeleton />}>
        <Routes>
          {/* Public. Declared before the guarded tree so it is never swallowed by it,
              and deliberately outside <ProtectedRoute> -- guarding the login page is
              the loop. */}
          <Route path="/login" element={<Login />} />
          {/* Public in-app webview continued from WhatsApp: /c#<token> */}
          <Route path="/c" element={<WebviewSessionPage />} />

          <Route element={<ProtectedRoute />}>
            {/* EVERY path that worked before this segment existed still works. A bookmark
                to /contacts/abc123 lands on /accounts/<theirs>/contacts/abc123 rather
                than the catch-all, which would have silently dropped them on the
                dashboard having eaten the id they asked for. */}
            <Route path="/" element={<LegacyPathRedirect />} />
            {LEGACY_PREFIXES.map((prefix) => (
              <Route key={prefix} path={`${prefix}/*`} element={<LegacyPathRedirect />} />
            ))}

            {/* A bare path with no account -- an old bookmark, or the post-login
                landing -- is sent to whichever account this person actually holds.
                Resolved rather than guessed: AccountLanding asks the server. */}
            {/* Outside the account segment on purpose: accepting is what GIVES this
                person an account, so there is no id to put in the URL yet. */}
            <Route path="/invite/:token" element={<Invite />} />

            {/* Where a teammate replaces the temporary password they were added with. Outside
                the account segment: until they do, the API opens no account at all. */}
            <Route path="/set-password" element={<SetPassword />} />

            {/* The PLATFORM admin area: its own workspace, outside every tenant account.
                It used to live at /accounts/:id/admin, inside one tenant's shell, which put
                a cross-tenant view under whichever customer's sidebar was open. */}
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<AdminDashboard />} />
              <Route path="branding" element={<PlatformBrandingPage />} />
              <Route path="billing" element={<BillingPage />} />
              {/* One tenant's own page: its cards as it sees them, and every control over it. */}
              <Route path=":accountId/account" element={<AdminTenantAccountPage />} />
              {/* Any other /admin/... lands on the index, never a blank shell. */}
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>

            <Route path="/accounts" element={<AccountLanding />} />
            <Route path="/accounts/:accountId" element={<AccountProvider><ShellOutlet onOpenPalette={() => setPaletteOpen(true)} /></AccountProvider>}>
            <Route path="dashboard" element={<ExecutiveOverview />} />

            {/* Inbox: the channel is a route param so all four inboxes are one
                component configured by data, per inboxuibuildbrief.md §1. The
                optional :threadId keeps a selected conversation deep-linkable. */}
            <Route path="inbox" element={<Inbox />} />
            <Route path="inbox/:channel" element={<Inbox />} />
            <Route path="inbox/:channel/:threadId" element={<Inbox />} />

            {/* List-detail: the detail segment renders as a slide-over ON TOP of the
                still-mounted list, so the list never unmounts and the URL stays
                refresh-safe. The page reads the param and renders the panel itself. */}
            <Route path="opportunities" element={<Pipeline />} />
            <Route path="opportunities/:dealId" element={<Pipeline />} />

            {/* /pipeline used to be the board and is now where pipelines are MANAGED.
                The old deal-detail URL redirects rather than 404s: the catch-all bounces
                to /dashboard, so a stale bookmark would silently land somewhere
                unrelated instead of on the deal it names. */}
            <Route path="pipeline" element={<Pipelines />} />
            <Route path="pipeline/:dealId" element={<LegacyDealRedirect />} />

            <Route path="contacts" element={<Contacts />} />
            <Route path="contacts/:contactId" element={<ContactDetail />} />

            <Route path="agent-studio" element={<AgentStudio />} />
            <Route path="agent-studio/flows" element={<Automations />} />
            <Route path="agent-studio/flow/:workflowId" element={<FlowCanvas />} />
            <Route path="agent-studio/history" element={<AgentHistory />} />
            <Route path="agent-studio/form-submissions" element={<FormSubmissions />} />
            <Route path="agent-studio/rules" element={<AutomationRules />} />
            <Route path="agent-studio/rules/:ruleId" element={<AutomationRules />} />

            <Route path="social" element={<SocialLayout />}>
              <Route index element={<Navigate to="composer" replace />} />
              <Route path="composer" element={<Composer />} />
              <Route path="engagement" element={<Engagement />} />
            </Route>

            {/* Accounts & assets and the four screens it links to. */}
            <Route path="connections" element={<Connections />} />
            <Route path="ads" element={<Ads />} />
            <Route path="catalog" element={<Catalog />} />
            <Route path="commerce" element={<Commerce />} />
            <Route path="lead-forms" element={<LeadForms />} />

            <Route path="performance" element={<Performance />} />
            <Route path="custom-fields" element={<CustomFields />} />
            <Route path="broadcast" element={<Broadcast />} />
            <Route path="broadcast/:campaignId" element={<Broadcast />} />
            {/* WhatsApp calls: /calls and /calls/<tab>. Not inbox/calls, which inbox/:channel owns. */}
            <Route path="calls/:tab?" element={<Calls />} />

            <Route path="settings" element={<Settings />} />
            <Route path="settings/team" element={<Team />} />
            <Route path="settings/developer" element={<Developer />} />
            <Route path="settings/billing" element={<WhatsAppBillingSettingsPage />} />
            <Route path="settings/branding" element={<BrandingSettingsPage />} />
            {/* The admin area moved out of the account. A bookmark to the old place goes
                to the new one, rather than falling through to the dashboard silently. */}
            <Route path="admin/*" element={<Navigate to="/admin" replace />} />
              {/* No screen named, or one that does not exist: stand wherever this
                  account's permissions allow. Both used to hardcode "dashboard", which
                  is the one page a Limited user cannot open. */}
              <Route index element={<AccountIndexRedirect />} />
              <Route path="*" element={<AccountIndexRedirect />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      </PageErrorBoundary>
    </>
  );
}

/** /pipeline/{dealId} was the board's deal URL before /pipeline became pipeline
 *  management. Redirect rather than let the catch-all bounce it to /dashboard, which
 *  would lose the deal the URL names. `replace` so Back does not bounce in a loop. */
function LegacyDealRedirect() {
  const { dealId } = useParams();
  // Relative, not absolute: this route already sits inside /accounts/:accountId, so a
  // leading slash would drop the account and bounce through the legacy redirect again.
  return <Navigate to={`../opportunities/${dealId ?? ""}`} replace />;
}

export default function App() {
  return (
    <AppProviders>
      <PlatformBrandingSync />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AppProviders>
  );
}
